import { useState, useEffect, useCallback } from 'react';
import { Button } from '../components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { RefreshCw, LogOut } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { AdminLoginForm } from '../components/admin/AdminLoginForm';
import { AdminStatsGrid } from '../components/admin/AdminStatsGrid';
import { QuotesTable } from '../components/admin/QuotesTable';
import { ContactsTable } from '../components/admin/ContactsTable';
import { HTTP_UNAUTHORIZED } from '../components/admin/admin-utils';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function AdminPage() {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [password, setPassword] = useState('');
  const [isLoggingIn, setIsLoggingIn] = useState(false);
  const [stats, setStats] = useState({ total_contacts: 0, total_quotes: 0, new_contacts: 0, new_quotes: 0 });
  const [contacts, setContacts] = useState([]);
  const [quotes, setQuotes] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [authHeader, setAuthHeader] = useState('');

  const handleLogin = async (e) => {
    e.preventDefault();
    setIsLoggingIn(true);
    const credentials = btoa(`admin:${password}`);
    const header = `Basic ${credentials}`;
    try {
      await axios.post(`${BACKEND_URL}/api/admin/login`, {}, {
        headers: { Authorization: header },
      });
      setAuthHeader(header);
      setIsAuthenticated(true);
      toast.success('Login successful');
    } catch {
      toast.error('Invalid password');
    }
    setIsLoggingIn(false);
  };

  const fetchData = useCallback(async () => {
    if (!authHeader) return;
    setIsLoading(true);
    try {
      const [statsRes, contactsRes, quotesRes] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/admin/stats`, { headers: { Authorization: authHeader } }),
        axios.get(`${BACKEND_URL}/api/admin/contacts`, { headers: { Authorization: authHeader } }),
        axios.get(`${BACKEND_URL}/api/admin/quotes`, { headers: { Authorization: authHeader } }),
      ]);
      setStats(statsRes.data);
      setContacts(contactsRes.data);
      setQuotes(quotesRes.data);
    } catch (error) {
      toast.error('Failed to fetch data');
      if (error.response?.status === HTTP_UNAUTHORIZED) {
        setIsAuthenticated(false);
        setAuthHeader('');
      }
    }
    setIsLoading(false);
  }, [authHeader]);

  useEffect(() => {
    if (isAuthenticated) {
      fetchData();
    }
  }, [isAuthenticated, fetchData]);

  const updateStatus = async (type, id, status) => {
    try {
      await axios.patch(
        `${BACKEND_URL}/api/admin/${type}/${id}/status`,
        { status },
        { headers: { Authorization: authHeader } }
      );
      toast.success('Status updated');
      fetchData();
    } catch {
      toast.error('Failed to update status');
    }
  };

  const deleteItem = async (type, id) => {
    try {
      await axios.delete(`${BACKEND_URL}/api/admin/${type}/${id}`, {
        headers: { Authorization: authHeader },
      });
      toast.success(`${type === 'contacts' ? 'Contact' : 'Quote'} deleted`);
      fetchData();
    } catch {
      toast.error('Failed to delete');
    }
  };

  const handleLogout = () => {
    setIsAuthenticated(false);
    setAuthHeader('');
    setPassword('');
    toast.info('Logged out');
  };

  if (!isAuthenticated) {
    return (
      <AdminLoginForm
        password={password}
        setPassword={setPassword}
        isLoggingIn={isLoggingIn}
        onSubmit={handleLogin}
      />
    );
  }

  return (
    <div className="min-h-screen pt-16 bg-slate-50" data-testid="admin-dashboard">
      {/* Header */}
      <div className="bg-slate-900 py-6">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center">
            <div>
              <h1 className="text-2xl font-black text-white" style={{ fontFamily: 'Chivo, sans-serif' }}>
                Admin Dashboard
              </h1>
              <p className="text-slate-400">Manage leads and quote requests</p>
            </div>
            <div className="flex items-center gap-4">
              <Button
                variant="outline"
                size="sm"
                onClick={fetchData}
                disabled={isLoading}
                className="border-slate-600 text-white hover:bg-slate-800"
                data-testid="refresh-btn"
              >
                <RefreshCw className={`w-4 h-4 mr-2 ${isLoading ? 'animate-spin' : ''}`} />
                Refresh
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={handleLogout}
                className="border-slate-600 text-white hover:bg-slate-800"
                data-testid="logout-btn"
              >
                <LogOut className="w-4 h-4 mr-2" />
                Logout
              </Button>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <AdminStatsGrid stats={stats} />

        <Tabs defaultValue="quotes" className="space-y-4">
          <TabsList>
            <TabsTrigger value="quotes" data-testid="tab-quotes">
              Quote Requests ({quotes.length})
            </TabsTrigger>
            <TabsTrigger value="contacts" data-testid="tab-contacts">
              Contact Submissions ({contacts.length})
            </TabsTrigger>
          </TabsList>

          <TabsContent value="quotes">
            <QuotesTable
              quotes={quotes}
              onStatusChange={(id, status) => updateStatus('quotes', id, status)}
              onDelete={(id) => deleteItem('quotes', id)}
            />
          </TabsContent>

          <TabsContent value="contacts">
            <ContactsTable
              contacts={contacts}
              onStatusChange={(id, status) => updateStatus('contacts', id, status)}
              onDelete={(id) => deleteItem('contacts', id)}
            />
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}
